import type { PaginatedData } from '../@types';
import { keepPreviousData, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiInstances, getData, hasApiMorePages } from '../utils/api';

type Pagination = {
  page: number;
  limit: number;
};

export type AdditionalQueryParams = {
  [key: string]: unknown;
};

export type QueryParams = Pagination & AdditionalQueryParams;

type PaginationQueryOptions<T> = Omit<UseQueryOptions<PaginatedData<T>>, 'queryKey' | 'queryFn'> & {
  queryKey?: UseQueryOptions<PaginatedData<T>>['queryKey'];
};

export const usePagination = <T>(
  endPoint: string,
  queryParams: Partial<QueryParams> = {},
  apiService: keyof ApiInstances = 'feed',
  options?: PaginationQueryOptions<T>,
  customHeaders?: Record<string, string>
) => {
  const [paginationState, setPaginationState] = useState({
    page: queryParams.page ?? 1,
    limit: queryParams.limit ?? 25,
  });
  const params = useMemo(() => ({ ...queryParams, ...paginationState }), [paginationState, queryParams]);
  const queryClient = useQueryClient();
  const { queryKey: additionalQueryKey, ...queryOptions } = options ?? {};

  const queryParamsString = JSON.stringify(queryParams);

  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  }, [queryParamsString]);

  const { data, isFetching, isPending, refetch, status } = useQuery({
    queryKey: [endPoint, params, additionalQueryKey],
    queryFn: (): Promise<PaginatedData<T>> => getData(endPoint, { ...params }, apiService, customHeaders),
    ...queryOptions,
    placeholderData: keepPreviousData,
  });

  const prefetchData = useCallback(
    (prefetchPage: number) => {
      queryClient.prefetchQuery({
        queryKey: [
          endPoint,
          {
            ...params,
            page: prefetchPage,
          },
        ],
        queryFn: () => getData(endPoint, { ...params, page: prefetchPage }, apiService),
      });
    },
    [queryClient, endPoint, params, apiService]
  );

  useEffect(() => {
    if (data && data.count && hasApiMorePages(data.count, paginationState.page, data.limit)) {
      prefetchData(paginationState.page + 1);
    }

    if (paginationState.page > 1) {
      prefetchData(paginationState.page - 1);
    }
  }, [data, prefetchData, paginationState.page]);

  const setPage = useCallback((page: number) => {
    setPaginationState((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPaginationState((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  return {
    data,
    isFetching,
    isPending,
    isLoading: isPending,
    items: (data?.items as T[]) ?? [],
    totalCount: data?.count ?? 0,
    pageCount: data?.count ? Math.ceil(data.count / paginationState.limit) : 0,
    activePage: paginationState.page,
    limit: paginationState.limit,
    setPage,
    setLimit,
    refetch,
    status,
  };
};
